import type { ChatMessagePartial } from '../useChatMessages/types';
import { ChatMessagePayloadType } from '../useChatMessages/types';
import type { FileMessagesState } from './types';

export function genFileMessageState(): FileMessagesState {
  return {
    map: {},
    pickInfo: undefined,
  };
}

export function browserDownloadFile(file: File) {
  const url = URL.createObjectURL(file);
  const domA = document.createElement('a');
  domA.download = file.name;
  domA.href = url;
  domA.click();
}

export function isFileMessage(msg: ChatMessagePartial) {
  return (
    msg.payload_type &&
    ChatMessagePayloadType.IMAGE <= msg.payload_type &&
    msg.payload_type <= ChatMessagePayloadType.FILE
  );
}

export async function generateThumbnail(
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    outputType?: string;
  } = {}
) {
  const { maxWidth = 200, maxHeight = 200, quality = 0.5, outputType } = options;

  function calculateAspectRatioFit(
    srcWidth: number,
    srcHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {
      width: srcWidth * ratio,
      height: srcHeight * ratio,
    };
  }

  return new Promise<{
    data: string;
    originalWidth: number;
    originalHeight: number;
    width: number;
    height: number;
  }>((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File type is not image.'));
      return;
    }

    const imgUrl = URL.createObjectURL(file);
    const img = new window.Image();

    img.onload = async () => {
      URL.revokeObjectURL(imgUrl);

      try {
        const { width, height } = calculateAspectRatioFit(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d')!;

        ctx.drawImage(img, 0, 0, width, height);

        resolve({
          data: await canvas.toDataURL(outputType || file.type, quality),
          originalWidth: img.width,
          originalHeight: img.height,
          width,
          height,
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(imgUrl);
      reject(new Error('Image load error.'));
    };

    img.src = imgUrl;
  });
}

export async function generateThumbnails(file: File) {
  const handleError = (e: Error) => {
    console.error(e);
  };

  return generateThumbnail(file, {
    maxWidth: 250,
    maxHeight: 250,
  }).catch(handleError);
}
